import ShowMoreHorizontalIcon from "@atlaskit/icon/core/show-more-horizontal";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {invalidateDataViewerQueriesAfterEventArchive} from "~/components/ottopilotDataViewer/invalidateOttopilotDataViewerQueries";
import {useState, type ReactNode} from "react";
import {ArchiveConfirmationModal} from "~/components/archiveConfirmationModal";
import {AnankeTempDropdownMenuWithIconButtonTrigger} from "~/components/anankeTemp/anankeTempDropdownMenuWithIconButtonTrigger";
import {CancelCallModal} from "~/components/dhiraaguCancelCallAction";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {OttopilotEventArchiveRuleKind, type OttopilotEventArchiveRule} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventArchiveConfig";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import type {EventWithEventContainer} from "~/utilities/apis/api";
import {archiveEventLikeArchiveDropdown} from "~/utilities/archiveEventContainerWithEvents";

function isArchiveValueEmpty(value: unknown): boolean {
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === "string") {
        return value.trim() === "";
    }
    return false;
}

export function getArchiveDisabledReasonFromRules({
    rules,
    additionalColumnValues,
}: {
    rules: Array<OttopilotEventArchiveRule>;
    additionalColumnValues: Record<string, unknown>;
}): string | null {
    for (const rule of rules) {
        const value = additionalColumnValues[rule.columnId];
        if (rule.kind === OttopilotEventArchiveRuleKind.requiresEmptyColumn) {
            if (isArchiveValueEmpty(value) === false) {
                return rule.disabledReason;
            }
            continue;
        }
        if (rule.kind === OttopilotEventArchiveRuleKind.requiresEqualsColumnValue) {
            if (value !== rule.expectedValue) {
                return rule.disabledReason;
            }
            continue;
        }
        if (rule.kind === OttopilotEventArchiveRuleKind.requiresNotEqualsColumnValue) {
            if (value === rule.expectedValue) {
                return rule.disabledReason;
            }
        }
    }
    return null;
}

/**
 * Archive menu item + modals for an event row. When the item is used inside another dropdown,
 * render `archiveDropdownItem` inside the menu and `archiveModals` as a sibling **outside** the
 * menu (e.g. next to the trigger). Otherwise the menu unmounts on close and the confirmation
 * modal never stays mounted.
 */
export function useEventArchiveAction({
    event,
    queryKeyPrefix,
    menuItemLabel = "Archive",
}: {
    event: EventWithEventContainer;
    queryKeyPrefix: Array<unknown>;
    menuItemLabel?: string;
}): {
    archiveDropdownItem: ReactNode;
    archiveModals: ReactNode;
} {
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
    const [cancelCallModal, setCancelCallModal] = useState<{
        event: EventWithEventContainer;
        queryKeyPrefix: Array<unknown>;
    } | null>(null);
    const {heimdallUser} = useWorkspaceUser();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {workspace, workspaceCapabilities} = useWorkspace();
    const queryClient = useQueryClient();
    const {showFlag} = useAnankeFlags();
    const eventTemplates = useEventTemplates();

    const eventTemplate = eventTemplates.find((template) => template.id === event.templateId);
    const statusColumnConfig = eventTemplate?.configuration.statusColumnConfig;

    const archiveMutation = useMutation({
        mutationFn: async () => {
            if (statusColumnConfig === undefined) {
                throw new Error("Status column configuration not found for this event type");
            }

            const result = await archiveEventLikeArchiveDropdown({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl: intellsysBaseUrl,
                workspaceId: workspace.id,
                workspace: workspace,
                workspaceCapabilities,
                event: event,
                eventTemplates: eventTemplates,
            });

            if (result.success !== true) {
                throw result.error;
            }
        },
        onSuccess: () => {
            showFlag({
                title: "Event archived",
                description: "The event was archived successfully.",
                appearance: "success",
                isAutoDismiss: true,
                autoDismissSeconds: 3,
            });
            setIsArchiveModalOpen(false);
            void invalidateDataViewerQueriesAfterEventArchive(queryClient, queryKeyPrefix, workspace.id);
        },
        onError: (error: unknown) => {
            const description = error instanceof Error ? error.message : "Could not archive the event. Please try again.";
            showFlag({
                title: "Archive failed",
                description: description,
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 4,
            });
        },
    });

    const archiveDisabledReason =
        statusColumnConfig === undefined
            ? null
            : getArchiveDisabledReasonFromRules({
                  rules: eventTemplate?.configuration.archiveConfig?.rules ?? [],
                  additionalColumnValues: event.additionalColumnValues,
              });
    const isArchiveDisabled = archiveDisabledReason !== null;

    const archiveDropdownItem =
        statusColumnConfig === undefined ? null : (
            <AnankeDropdownItem
                onClick={() => {
                    if (isArchiveDisabled === true) {
                        showFlag({
                            title: "Cannot Archive",
                            description: archiveDisabledReason ?? "Archive is not allowed for this event",
                            appearance: "warning",
                            isAutoDismiss: true,
                            autoDismissSeconds: 4,
                        });
                        return;
                    }
                    setIsArchiveModalOpen(true);
                }}
                isDisabled={isArchiveDisabled}
            >
                {menuItemLabel}
            </AnankeDropdownItem>
        );

    const archiveModals =
        statusColumnConfig === undefined ? null : (
            <>
                <ArchiveConfirmationModal
                    isOpen={isArchiveModalOpen}
                    setIsOpen={setIsArchiveModalOpen}
                    itemType="event"
                    onConfirm={async () => {
                        await archiveMutation.mutateAsync();
                    }}
                    isLoading={archiveMutation.isPending}
                />

                {cancelCallModal !== null ? (
                    <CancelCallModal
                        event={cancelCallModal.event}
                        queryKeyPrefix={cancelCallModal.queryKeyPrefix}
                        isOpen={true}
                        setIsOpen={(open) => {
                            if (open === false) {
                                setCancelCallModal(null);
                            }
                        }}
                    />
                ) : null}
            </>
        );

    return {
        archiveDropdownItem: archiveDropdownItem,
        archiveModals: archiveModals,
    };
}

export function EventArchiveActionDropdown({event, queryKeyPrefix}: {event: EventWithEventContainer; queryKeyPrefix: Array<unknown>}) {
    const {archiveDropdownItem, archiveModals} = useEventArchiveAction({
        event: event,
        queryKeyPrefix: queryKeyPrefix,
    });

    if (archiveDropdownItem === null) {
        return null;
    }

    return (
        <>
            <AnankeTempDropdownMenuWithIconButtonTrigger
                iconButtonProps={{
                    type: "button",
                    icon: ShowMoreHorizontalIcon,
                    label: "More actions",
                    appearance: "subtle",
                    spacing: "compact",
                }}
            >
                <AnankeDropdownItemGroup>{archiveDropdownItem}</AnankeDropdownItemGroup>
            </AnankeTempDropdownMenuWithIconButtonTrigger>

            {archiveModals}
        </>
    );
}
